import { useState } from "react";
import { ExternalLink, Globe } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface WebLinkChipProps {
  /** The link URL */
  url: string;
  /** Optional display label (defaults to hostname) */
  label?: string;
  /** Custom class name */
  className?: string;
}

function getHostname(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

/**
 * Compact chip for rendering web links with a favicon.
 * Styled like DocChip (neutral gray) with an external-link indicator.
 */
export function WebLinkChip({ url, label, className }: WebLinkChipProps) {
  const [faviconError, setFaviconError] = useState(false);
  const hostname = getHostname(url);
  const displayLabel = label || hostname;
  const faviconUrl = `https://www.google.com/s2/favicons?domain=${hostname}&sz=16`;

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "inline-flex h-6 max-w-[280px] items-center gap-1.5 rounded px-2",
              "bg-neutral-100 hover:bg-neutral-200 active:bg-neutral-300",
              "text-xs !text-neutral-700 !no-underline transition-colors hover:!text-neutral-700 hover:!no-underline",
              "focus:outline-none focus:ring-2 focus:ring-neutral-400 focus:ring-offset-1",
              className
            )}
          >
            {faviconError ? (
              <Globe className="h-3.5 w-3.5 flex-shrink-0 text-neutral-500" />
            ) : (
              <img
                src={faviconUrl}
                alt=""
                className="h-3.5 w-3.5 flex-shrink-0"
                onError={() => setFaviconError(true)}
              />
            )}
            <span className="min-w-0 truncate">{displayLabel}</span>
            <ExternalLink className="h-3 w-3 flex-shrink-0 text-neutral-400" />
          </a>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <p className="break-all text-sm">{url}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
